import { z } from "zod";
import { useUrlSearchState } from "../presentation/useUrlSearchState";

const BatchFiltersSchema = z.object({
	chainId: z.coerce.number().int().optional(),
	anchored: z
		.enum(["true", "false"])
		.optional()
		.transform((v) => (v === undefined ? undefined : v === "true")),
	page: z.coerce.number().int().min(1).optional().default(1),
});

export type BatchFilters = z.infer<typeof BatchFiltersSchema>;

export interface UseBatchFiltersReturn {
	filters: BatchFilters;
	setFilter: <K extends keyof BatchFilters>(
		key: K,
		value: BatchFilters[K],
	) => void;
	removeFilter: (key: keyof BatchFilters) => void;
	clearAll: () => void;
}

/**
 * URL-persisted filter state for the batch explorer page.
 * Uses useUrlSearchState so filters survive page refresh and are shareable via URL.
 */
export function useBatchFilters(): UseBatchFiltersReturn {
	const [filters, setPartial] = useUrlSearchState(BatchFiltersSchema);

	return {
		filters,
		setFilter: (key, value) => setPartial({ [key]: value }),
		removeFilter: (key) => {
			const next = { ...filters } as Partial<BatchFilters>;
			delete next[key];
			setPartial(next as BatchFilters);
		},
		clearAll: () =>
			setPartial({ chainId: undefined, anchored: undefined, page: 1 }),
	};
}
